import {
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EntitySelect } from '../../components/EntityField';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import { useLocationId } from '../../hooks/useSeedLocation';
import { formatMoney, formatQty, type NumberInputValue, typedNumber } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { COUNTS_PATH, LEDGER_PATH, STOCK_PATH, useStockItems, useStockOnHand, useUnitOptions } from './stock';
import { Known, type StockActionProps } from './subject';

interface CountLine {
  /** Lines are added and removed while the form is open, so each carries its own React key. */
  key: string;
  inventoryItemId: string;
  countedQty: NumberInputValue;
  /**
   * What he is holding, which is not always what the shelf is counted in.
   *
   * Empty means "the item's own unit" — resolved when the line is sent rather than stored, because
   * the sheet is built before the item on a line the counter adds himself.
   */
  countedIn: string;
  /** True when the books already say this is in the area, false when the counter found it there. */
  onSheet: boolean;
}

let keySeq = 0;
const blankLine = (inventoryItemId = '', onSheet = false): CountLine => ({
  key: `line-${++keySeq}`,
  inventoryItemId,
  countedQty: '',
  countedIn: '',
  onSheet,
});

interface CountResultLine {
  id: string;
  inventoryItemId: string;
  expectedQty: number | null;
  countedQty: number;
  unit: string;
  varianceQty: number | null;
  varianceCostBaseMinor: number | null;
  /** What was written on the sheet, when it was not the shelf's own unit. Null when it was. */
  enteredQty: number | null;
  enteredUnit: string | null;
}

interface CountResult {
  count: { id: string; storageLocationId: string };
  lines: CountResultLine[];
}

/**
 * Counting an area and approving the result is the only way a wrong shelf figure can be corrected
 * — there is no "edit the number" anywhere in this system, by design, because a stock figure with
 * no movement behind it is a figure nobody can explain a month later.
 *
 * The sheet is filled in FROM THE AREA, which is the difference between this and a data-entry
 * form: counting the bar at close used to mean adding forty lines by hand, each chosen from a
 * dropdown of every item the business buys, with nothing on screen to say what was meant to be on
 * that shelf. Anything left off simply kept its old figure, silently. Quantities stay blank —
 * a count where the expected number is already in the box is not a count.
 *
 * The two steps are deliberate and match the endpoint: submitting freezes what was counted, and
 * nothing moves until the differences have been read and approved.
 */
export function CountAreaButton({
  currency,
  storageLocationId: knownArea,
  label,
  compact,
}: { currency: string } & StockActionProps) {
  const queryClient = useQueryClient();
  const locationId = useLocationId();
  const areas = useEntityIndex('storageLocation');
  const [opened, setOpened] = useState(false);
  const [storageLocationId, setStorageLocationId] = useState(knownArea ?? '');
  const [lines, setLines] = useState<CountLine[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CountResult | null>(null);
  /** Which area's sheet is already on screen, so typing is never wiped by a background refetch. */
  const [sheetFor, setSheetFor] = useState('');

  const items = useEntityIndex('inventoryItem');
  const stock = useStockItems();
  const unitOptions = useUnitOptions();
  const held = useStockOnHand(storageLocationId || undefined);

  useEffect(() => {
    if (!storageLocationId || held.isLoading || sheetFor === storageLocationId) return;
    const sheet = held.rows
      .filter((r) => r.qtyOnHand !== 0)
      .map((r) => blankLine(r.inventoryItemId, true))
      .sort((a, b) => stock.nameOf(a.inventoryItemId).localeCompare(stock.nameOf(b.inventoryItemId)));
    setLines(sheet.length > 0 ? sheet : [blankLine()]);
    setSheetFor(storageLocationId);
  }, [storageLocationId, held.isLoading, held.rows, sheetFor, stock.nameOf]);

  const close = () => {
    setOpened(false);
    setStorageLocationId(knownArea ?? '');
    setLines([]);
    setSheetFor('');
    setError(null);
    setResult(null);
  };

  const refreshStock = () => {
    void queryClient.invalidateQueries({ queryKey: listKey(STOCK_PATH) });
    void queryClient.invalidateQueries({ queryKey: listKey(LEDGER_PATH) });
  };

  /** A sheet that has been frozen is now waiting for somebody, approved here or not. */
  const refreshWaiting = () => void queryClient.invalidateQueries({ queryKey: listKey(COUNTS_PATH) });

  const counted = lines.filter((l) => l.inventoryItemId && typedNumber(l.countedQty) !== undefined);
  const skipped = lines.filter((l) => l.onSheet && typedNumber(l.countedQty) === undefined).length;

  const submit = useMutation({
    mutationFn: () =>
      rawApi<CountResult>('POST', '/admin/v1/inventory/stock-counts', {
        locationId,
        storageLocationId,
        // Said rather than asked: the sheet already knows whether everything on it was counted, and
        // asking produced an answer that could contradict what was actually filled in.
        kind: skipped === 0 ? 'full' : 'cycle',
        lines: counted.map((l) => ({
          inventoryItemId: l.inventoryItemId,
          countedQty: Number(l.countedQty),
          // What he counted IN, which the API converts to what the shelf balances in. Only ever
          // one of the units the API said it would take, so the conversion is on file by
          // construction; it refuses one it cannot convert rather than guessing at a factor of 1.
          unit: l.countedIn || stock.unitOf(l.inventoryItemId),
        })),
      }),
    onSuccess: (data) => {
      setResult(data);
      refreshWaiting();
    },
    onError: (err) => notifyError(err, 'save this count'),
  });

  const approve = useMutation({
    mutationFn: () => rawApi('POST', `/admin/v1/inventory/stock-counts/${result?.count.id}/approve`),
    onSuccess: () => {
      refreshStock();
      refreshWaiting();
      notifyDone('Count approved', 'The shelf figures now match what you counted.');
      close();
    },
    onError: (err) => notifyError(err, 'approve this count'),
  });

  const start = () => {
    if (!storageLocationId) return setError('Choose which area you counted');
    if (counted.length === 0) return setError('Write in what you counted for at least one item');
    const noUnit = counted.find((l) => !stock.unitOf(l.inventoryItemId));
    if (noUnit) {
      return setError(
        `${items.label(noUnit.inventoryItemId)} has no unit set — fill in "Counted in" under Stock items first`,
      );
    }
    // The sheet already lists everything the books hold here, so a line added at the bottom for
    // something further up is a duplicate. Both would be measured against the same starting figure
    // and both applied on approval, leaving the shelf further out than before the count.
    const seen = new Set<string>();
    const twice = counted.find((l) => {
      if (seen.has(l.inventoryItemId)) return true;
      seen.add(l.inventoryItemId);
      return false;
    });
    if (twice) {
      return setError(
        `${items.label(twice.inventoryItemId)} is on the sheet twice — put the whole amount on one line and remove the other`,
      );
    }
    setError(null);
    submit.mutate();
  };

  const setLine = (index: number, patch: Partial<CountLine>) =>
    setLines(lines.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  return (
    <>
      <Button
        size={compact ? 'compact-xs' : 'sm'}
        variant={compact ? 'subtle' : 'light'}
        onClick={() => setOpened(true)}
      >
        {label ?? 'Count an area'}
      </Button>
      <Modal
        opened={opened}
        onClose={close}
        title={result ? 'What the count found' : 'Count an area'}
        size="xl"
        closeOnClickOutside={false}
      >
        {result ? (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Nothing has changed yet. Approving writes each difference below into the stock record as a count
              correction and puts its cost against the bar. That is what makes the shelf figures match what
              you counted.
            </Text>
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Item</Table.Th>
                  <Table.Th>Expected</Table.Th>
                  <Table.Th>You counted</Table.Th>
                  <Table.Th>Difference</Table.Th>
                  <Table.Th>Worth</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {result.lines.map((line) => (
                  <Table.Tr key={line.id}>
                    <Table.Td>{items.label(line.inventoryItemId)}</Table.Td>
                    <Table.Td>{formatQty(line.expectedQty ?? 0, line.unit)}</Table.Td>
                    <Table.Td>
                      {formatQty(line.countedQty, line.unit)}
                      {/* What he actually wrote, when he counted in something else. The row above
                          is the balance and has to be in one unit to be readable at all; this is
                          the record, and it is what he would be shown if the adjustment were
                          questioned. */}
                      {line.enteredUnit && (
                        <Text size="xs" c="dimmed">
                          you wrote {formatQty(line.enteredQty, line.enteredUnit)}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td c={(line.varianceQty ?? 0) < 0 ? 'red' : undefined}>
                      {(line.varianceQty ?? 0) > 0 ? '+' : ''}
                      {formatQty(line.varianceQty ?? 0, line.unit)}
                    </Table.Td>
                    <Table.Td c={(line.varianceCostBaseMinor ?? 0) < 0 ? 'red' : undefined}>
                      {formatMoney(line.varianceCostBaseMinor ?? 0, currency)}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {/* This used to say the sheet could never be opened again, which was true and was the
                defect: nothing listed a waiting count and nothing could retire one, so leaving this
                dialogue locked the area for ever — the API refuses a second sheet while a frozen one
                is outstanding, and rightly. "Counts waiting for you" on the On hand tab is where it
                goes now, and this says where rather than warning about a dead end. */}
            <Alert color="yellow" title="Nothing has moved yet">
              Leave it unapproved and it waits for you under &ldquo;On hand&rdquo;, where you can approve it
              or throw it away. Until you do one or the other, this area cannot be counted again.
            </Alert>
            <Group justify="flex-end">
              <Button variant="default" onClick={close} disabled={approve.isPending}>
                Leave it unapproved
              </Button>
              <Button loading={approve.isPending} onClick={() => approve.mutate()}>
                Approve the count
              </Button>
            </Group>
          </Stack>
        ) : (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Write down what is actually on the shelf. You will see the difference against what the system
              expected before anything changes.
            </Text>

            {knownArea ? (
              <Known label="Which area you counted" value={areas.label(knownArea)} />
            ) : (
              <EntitySelect
                entity="storageLocation"
                label="Which area you counted"
                required
                value={storageLocationId || null}
                onChange={(v) => {
                  setStorageLocationId(v ?? '');
                  setSheetFor('');
                  setLines([]);
                }}
              />
            )}

            {storageLocationId && held.isLoading && (
              <Group gap="xs">
                <Loader size="xs" />
                <Text size="sm" c="dimmed">
                  Fetching what should be in there…
                </Text>
              </Group>
            )}

            {storageLocationId && !held.isLoading && (
              <div>
                <Text size="sm" fw={500}>
                  What you counted
                </Text>
                <Text size="xs" c="dimmed" mb={6}>
                  Everything the books say is in this area, in the unit each one is counted in. Write in what
                  you actually find — including nought for anything that has run out.
                </Text>
                <Stack gap="xs">
                  {lines.map((line, index) => {
                    const unit = stock.unitOf(line.inventoryItemId);
                    /*
                     * WHAT HE IS HOLDING.
                     *
                     * The beer is bought by the case and stands on the shelf in bottles, and this
                     * form used to send the shelf's unit whatever he typed — so counting the bar
                     * meant dividing 240 by 24 in his head, or typing 240 and having the sheet read
                     * it as 240 cases. The API refuses that now, which saves the shelf and does
                     * nothing at all for the man with the clipboard. The picker is the other half.
                     *
                     * Only shown when there is a real choice: a second control on a line for
                     * something counted one way is a control that only ever says the same word.
                     */
                    const options = unitOptions(line.inventoryItemId, unit);
                    return (
                      <Paper key={line.key} withBorder p="xs" radius="sm">
                        <Group gap="xs" align="flex-end" wrap="wrap">
                          <div style={{ minWidth: 260, flex: 1 }}>
                            {line.onSheet ? (
                              <>
                                <Text size="xs" c="dimmed">
                                  Item
                                </Text>
                                <Text size="sm" fw={500}>
                                  {items.label(line.inventoryItemId)}
                                </Text>
                              </>
                            ) : (
                              <EntitySelect
                                entity="inventoryItem"
                                label="Item"
                                value={line.inventoryItemId || null}
                                onChange={(v) => setLine(index, { inventoryItemId: v ?? '' })}
                              />
                            )}
                          </div>
                          <NumberInput
                            label="Counted"
                            min={0}
                            style={{ width: 170 }}
                            suffix={options.length > 1 || !unit ? undefined : ` ${unit}`}
                            value={line.countedQty}
                            onChange={(v) => setLine(index, { countedQty: v })}
                          />
                          {options.length > 1 && (
                            <Select
                              label="Counted in"
                              style={{ width: 140 }}
                              data={options}
                              allowDeselect={false}
                              value={line.countedIn || unit}
                              onChange={(v) => setLine(index, { countedIn: v ?? '' })}
                            />
                          )}
                          {!line.onSheet && (
                            <Button
                              size="compact-sm"
                              variant="subtle"
                              color="red"
                              onClick={() => setLines(lines.filter((_, i) => i !== index))}
                            >
                              Remove
                            </Button>
                          )}
                        </Group>
                      </Paper>
                    );
                  })}
                  <Button
                    size="compact-sm"
                    variant="light"
                    style={{ alignSelf: 'flex-start' }}
                    onClick={() => setLines([...lines, blankLine()])}
                  >
                    Add something else you found
                  </Button>
                </Stack>
              </div>
            )}

            {/* Left blank is not the same as counted as nought, and the difference is a shelf
                figure that quietly stays wrong.

                Only once something has actually been written in. A freshly opened sheet is blank
                by definition, and warning that it is blank before the counter has touched it is a
                warning about nothing — which is how people learn to read past the one that matters.
                It appears the moment the sheet is part-filled, which is when it does. */}
            {skipped > 0 && counted.length > 0 && (
              <Alert color="yellow" variant="light" title="Some of the sheet is still blank">
                {skipped === 1 ? 'One item has' : `${skipped} items have`} no number written in. Those figures
                will be left exactly as they are. Write in nought for anything that has run out.
              </Alert>
            )}

            {error && (
              <Text size="sm" c="red">
                {error}
              </Text>
            )}

            <Group justify="flex-end">
              <Button variant="default" onClick={close} disabled={submit.isPending}>
                Cancel
              </Button>
              <Button loading={submit.isPending} onClick={start} disabled={!storageLocationId}>
                See the differences
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
